<template>
  <WdsStack direction-row justify-space-between gap-md class="tw-mb-8">
    <WdsStack direction-column gap-md>
      <WdsStack direction-row align-start gap-md>
        <WdsHeading as="h1" type="display">
          {{ $t(':number Divisions', { number: paginatorInfo?.total ?? 0 }) }}
        </WdsHeading>
      </WdsStack>
      <WdsStack direction-column justify-space-between gap-sm>
        <WdsText xl secondary>
          {{ $t('Companies that are a part of your organisation.') }}
        </WdsText>
      </WdsStack>
    </WdsStack>

    <slot name="right">
      <WdsStack direction-row justify-space-between align-end gap-sm>
        <WdsText>
          {{ $t('Need help?') }}
          <a href="#" class="intercomchat">{{ $t('Chat with us') }}.</a>
        </WdsText>
      </WdsStack>
    </slot>
  </WdsStack>

  <BaseDataTable
    :entries
    :columns
    :loading
    :paginator-info="paginatorInfo"
    search-disabled
    @search="updateData"
  >
    <template #Name="{ row }">
      <WdsAvatarLabel
        md
        :text="row.name"
        :path="row.avatar"
        :sub-text="
          row.address.city
            ? `${row.address.city}, ${row.address.country.name}`
            : row.address.country.name
        "
      ></WdsAvatarLabel>
    </template>
    <template #TrustedContacts="{ row }">
      <WdsText :text="`${row.trustedContacts.paginatorInfo.total}`" />
    </template>
    <template #TeamMembers="{ row }">
      <WdsText :text="`${row.teamMembers.paginatorInfo.total}`" />
    </template>
    <template #SwitchTeam="{ row }">
      <a
        :href="`/users/${row.id}/switchteam`"
        :title="$t('Switch team to :name', { name: row.name })"
      >
        <WdsIcon name="compare"></WdsIcon>
      </a>
    </template>
  </BaseDataTable>
</template>

<script setup lang="ts">
import { WdsAvatarLabel, WdsHeading, WdsIcon, WdsStack, WdsText } from '@worksome/wds-components'
import { computed, reactive } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import { useFragment } from '@/gql'
import { PaginatorInfoFragment } from '@/components/GraphQL/fragments/paginatorInfoFragment'
import { DivisionsQuery } from '@/components/Organisations/Divisions/graphql/queries'

const columns = [
  {
    slot: 'Name',
    key: 'name',
    title: $t('Name'),
    order: 'asc',
    orderAble: true,
    searchKeys: ['name'],
  },
  {
    slot: 'TrustedContacts',
    key: 'trustedContacts',
    title: $t('Trusted contacts'),
  },
  {
    slot: 'TeamMembers',
    key: 'teamMembers',
    title: $t('Team members'),
  },
  {
    slot: 'SwitchTeam',
    key: 'switchTeam',
    title: $t('Switch team'),
  },
]

const pagination = reactive({
  page: 1,
  first: 50,
})

interface BaseDataTableSearchSettings {
  page: number
  perPage: number
}

const updateData = (searchSettings: BaseDataTableSearchSettings) => {
  pagination.page = searchSettings.page + 1
  pagination.first = searchSettings.perPage
}

const { result, loading } = useQuery(
  DivisionsQuery,
  () => ({
    organisation: currentAccount.globalId,
    first: pagination.first,
    page: pagination.page,
  }),
  {
    clientId: 'publicApi',
  },
)

const entries = computed(() => {
  if (loading.value) {
    return []
  }

  return result.value?.organisation?.divisions.data
})

const paginatorInfo = computed(
  () =>
    useFragment(PaginatorInfoFragment, result.value?.organisation?.divisions.paginatorInfo) ??
    undefined,
)
</script>

<style scoped lang="scss"></style>
